import type { LC, LiveElement } from '@use-gpu/live';
import type { TypedArray, VectorLike, VectorLikes } from '@use-gpu/core';
import type { EaseType } from './types';

import { seq } from '@use-gpu/core';
import { extend, mutate, fence, useCallback, useDouble, useMemo, useOne, useNoOne, useRef } from '@use-gpu/live';
import { useTimeContext } from '../providers/time-provider';
import { useAnimationFrame, useNoAnimationFrame } from '../providers/loop-provider';
import { getRenderFunc } from '../hooks/useRenderProp';

import { EaseTypes, cloneLerpable, copyLerpable } from './interpolate';

import mapValues from 'lodash/mapValues.js';
import zipObject from 'lodash/zipObject.js';

export type EaseToTargetProps<T extends Record<string, number | VectorLike | VectorLikes>> = {
  smooth?: number,

  duration?: number,
  speed?: number,
  paused?: boolean,
  epsilon?: number,

  values: T,
  types?: Record<string, EaseType>,
  version?: number,

  render?: (values: T) => LiveElement,
  children?: LiveElement | ((values: T) => LiveElement),
};

// causes typescript docgen to crash if defined as recursive
type NestedNumberArray = any[];
type Numberish = number | TypedArray | NestedNumberArray;

export const EaseToTarget: LC<EaseToTargetProps<Record<string, Numberish>>> = <T extends Record<string, Numberish>>(
  props: EaseToTargetProps<T>
) => {
  const {
    smooth = 1,

    duration = 0.1,
    speed = 1,
    paused = false,
    epsilon = 1e-3,

    values: target,
    types,
    version = 0,

    children,
  } = props;

  const render = getRenderFunc(props);

  const keys = Object.keys(target);

  // Construct interpolators once
  const easeTypes = useMemo(() =>
    mapValues(target, (value, k) => {
      const t = types?.[k] ?? 'number';
      const et = EaseTypes[t];
      if (!et) throw new Error(`Unknown ease value type '${t}' for '${k}'`);
      return et;
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  , [...keys, types]);

  // To avoid garbage collection, make a double-buffered value object with copies of all values
  const [swapValues] = useDouble(() => mapValues(target, v => cloneLerpable(v)), keys);

  // If rendering JSX children, optimize this too
  const [swapElements] = useDouble(() => children && !render ? extend(children, swapValues()) : null, [children, swapValues]);

  // Track current values + intermediates
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const trackedValues = useMemo(() => seq(smooth).map(() => mapValues(target, v => cloneLerpable(v))), [smooth, swapValues, version]);

  // But scalars can't be passed by reference, so track them
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const scalars = useMemo(() => zipObject(keys.filter(k => typeof target[k] === 'number')), keys);

  // Pass new target by ref
  const targetRef = useRef(target);
  targetRef.current = target;

  // Static continuation callback
  const Run = useCallback(() => {
    const {elapsed, delta} = useTimeContext();
    const current = swapValues();
    const {current: target} = targetRef;

    // Track max distance
    let maxDistance = 0;
    for (const k in current) {
      const d = easeTypes[k].measure(current[k], target[k]);
      maxDistance = Math.max(maxDistance, d);
    }

    const finished = (delta && (maxDistance < epsilon)) || (duration <= 0);

    // Interpolate values
    if (!finished) {
      useOne(() => {
        if (!delta) return;

        const fraction = 1 - Math.pow(2, -(delta * speed) / 1000 / duration);

        for (let i = 0; i < smooth; ++i) {
          const a = trackedValues[i - 1] ?? target;
          const b = trackedValues[i];

          for (const k in current) {
            b[k] = easeTypes[k].lerp(b[k], b[k], a[k], fraction);
          }
        }

        for (const k in current) {
          current[k] = copyLerpable(current[k], trackedValues[smooth - 1][k]);
        }
      }, elapsed);
    }
    // Snap to target for clean exit
    else {
      useNoOne();
      for (const k in current) {
        current[k] = copyLerpable(current[k], target[k]);
      }
    }

    // Run if not paused or not converged
    if (!paused && !finished) useAnimationFrame();
    else useNoAnimationFrame();

    if (render) return render(current);
    else if (typeof children === 'object') {
      const elements = swapElements();
      for (const k in scalars) scalars[k] = current[k];
      mutate(elements, scalars);
      return elements;
    }

    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swapValues, swapElements, trackedValues, scalars, duration, speed, smooth, paused, render, children]);

  // Fence so that only continuation runs repeatedly
  return fence(null, Run);
};
